import { resourcesCopy } from '@/copy/resources'
import { CodeBlock } from '@/ui/CodeBlock/CodeBlock'
import { EmptyState } from '@/ui/EmptyState/EmptyState'
import { Icon } from '@/ui/Icon/Icon'
import { Inline } from '@/ui/layout/Inline'
import { Stack } from '@/ui/layout/Stack'
import { Text } from '@/ui/layout/Text'
import { Fact, Facts } from '../Facts'
import styles from './ResourceViewBodies.module.css'
import type { ViewResource } from './resourceViews'

/**
 * Per-kind read-side body renderers for the `/r/:id` detail page —
 * presentational components only. The kind→descriptor registry lives in
 * `resourceViews.ts`.
 */

function TextBlock({
  resource,
  text,
}: {
  resource: ViewResource
  text: string
}) {
  return (
    <CodeBlock title={resource.id} caption={resource.name || resource.kind}>
      {text}
    </CodeBlock>
  )
}

export function NoteBody({ resource }: { resource: ViewResource }) {
  return resource.data.content ? (
    <TextBlock resource={resource} text={String(resource.data.content)} />
  ) : (
    <EmptyState variant="inline" description={resourcesCopy.view.noteEmpty} />
  )
}

export function PlanBody({ resource }: { resource: ViewResource }) {
  return (
    <Stack gap="space-12">
      {resource.data.plan_text ? (
        <TextBlock resource={resource} text={String(resource.data.plan_text)} />
      ) : null}
      {resource.data.annotations ? (
        <Text as="p" tone="text-mute">
          “{String(resource.data.annotations)}”
        </Text>
      ) : null}
    </Stack>
  )
}

export function QuestionBody({ resource }: { resource: ViewResource }) {
  return (
    <Stack gap="space-12">
      {resource.data.question ? (
        <TextBlock resource={resource} text={String(resource.data.question)} />
      ) : null}
      {Array.isArray(resource.data.options) &&
        resource.data.options.length > 0 && (
          <Stack as="ul" gap="space-4" className={styles.list}>
            {(
              resource.data.options as { label?: unknown; value?: unknown }[]
            ).map((o) => (
              <Text
                as="li"
                tone="text-sub"
                key={String(o.value ?? o.label ?? '')}
              >
                {String(o.label ?? o.value ?? '')}
              </Text>
            ))}
          </Stack>
        )}
      {resource.data.answer != null && (
        <Inline gap="space-8" align="baseline">
          <Text variant="caption" tone="text-mute">
            {resourcesCopy.view.answer}
          </Text>
          <Text tone="text">{String(resource.data.answer)}</Text>
        </Inline>
      )}
    </Stack>
  )
}

export function TodoBody({ resource }: { resource: ViewResource }) {
  const items = (resource.data.items ?? []) as {
    text?: unknown
    completed?: boolean
  }[]
  if (items.length === 0) {
    return (
      <EmptyState variant="inline" description={resourcesCopy.view.todoEmpty} />
    )
  }
  return (
    <Stack as="ul" gap="space-4" className={styles.todos}>
      {items.map((item, i) => (
        <Inline
          as="li"
          // biome-ignore lint/suspicious/noArrayIndexKey: todo rows carry no stable id; text-only keys collide on duplicates
          key={i}
          gap="space-8"
          wrap={false}
        >
          {item.completed ? (
            <Icon
              name="check"
              size="icon-inline"
              label={resourcesCopy.todo.done}
            />
          ) : (
            <span className={styles.box} aria-hidden="true" />
          )}
          <Text tone={item.completed ? 'text-mute' : 'text-sub'}>
            {String(item.text ?? '')}
          </Text>
        </Inline>
      ))}
    </Stack>
  )
}

export function WebBody({ resource }: { resource: ViewResource }) {
  return (
    <Stack gap="space-12">
      <Facts>
        <Fact label={resourcesCopy.view.fact.url}>
          {String(resource.data.url ?? '—')}
        </Fact>
      </Facts>
      {resource.data.content ? (
        <TextBlock resource={resource} text={String(resource.data.content)} />
      ) : null}
    </Stack>
  )
}

export function McpBody({ resource }: { resource: ViewResource }) {
  return (
    <Facts>
      <Fact label={resourcesCopy.view.fact.endpoint}>
        {String(resource.data.url ?? '—')}
      </Fact>
    </Facts>
  )
}

export function GitRepoBody({ resource }: { resource: ViewResource }) {
  return (
    <Facts>
      <Fact label={resourcesCopy.view.fact.remote}>
        {String(resource.data.remote_url ?? '—')}
      </Fact>
      <Fact label={resourcesCopy.view.fact.branch}>
        {String(resource.data.branch ?? resourcesCopy.view.defaultValue)}
      </Fact>
    </Facts>
  )
}

export function GitHubBody({ resource }: { resource: ViewResource }) {
  const d = resource.data
  if (d.auth === 'app') {
    return (
      <Facts>
        <Fact label={resourcesCopy.view.fact.account}>
          {String(d.account_login ?? '—')}
        </Fact>
        <Fact label={resourcesCopy.view.fact.status}>
          {String(d.status ?? 'active')}
        </Fact>
        <Fact label={resourcesCopy.view.fact.repositories}>
          {Array.isArray(d.repositories) && d.repositories.length > 0
            ? d.repositories.join(', ')
            : resourcesCopy.view.allRepositories}
        </Fact>
      </Facts>
    )
  }
  return (
    <Facts>
      <Fact label={resourcesCopy.view.fact.url}>
        {String(resource.data.url ?? '—')}
      </Fact>
      <Fact label={resourcesCopy.view.fact.toolsets}>
        {String(resource.data.toolsets ?? resourcesCopy.view.defaultValue)}
      </Fact>
    </Facts>
  )
}

export function SqliteBody({ resource }: { resource: ViewResource }) {
  return (
    <Facts>
      <Fact label={resourcesCopy.view.fact.path}>
        {resource.data.path ? (
          <Text variant="code" tone="text-sub">
            {String(resource.data.path)}
          </Text>
        ) : (
          '—'
        )}
      </Fact>
    </Facts>
  )
}

export function WebhookBody({ resource }: { resource: ViewResource }) {
  return (
    <Facts>
      <Fact label={resourcesCopy.view.fact.event}>
        {String(resource.data.event_type ?? resourcesCopy.view.any)}
      </Fact>
      {resource.data.description ? (
        <Fact label={resourcesCopy.view.fact.note}>
          {String(resource.data.description)}
        </Fact>
      ) : null}
    </Facts>
  )
}

export function CronBody({ resource }: { resource: ViewResource }) {
  return (
    <Facts>
      <Fact label={resourcesCopy.view.fact.schedule}>
        {String(resource.data.schedule ?? resourcesCopy.view.notSet)}
        {resource.data.one_shot ? ` · ${resourcesCopy.view.oneShot}` : ''}
        {resource.data.enabled === false
          ? ` · ${resourcesCopy.view.disabled}`
          : ''}
      </Fact>
      {resource.data.task ? (
        <Fact label={resourcesCopy.view.fact.task}>
          {String(resource.data.task)}
        </Fact>
      ) : null}
    </Facts>
  )
}

export function GenericJsonBody({ resource }: { resource: ViewResource }) {
  return (
    <TextBlock
      resource={resource}
      text={JSON.stringify(resource.data, null, 2)}
    />
  )
}
